import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, Download, FileSpreadsheet, Loader2, X,
} from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { formatMoney, formatNumber } from '../lib/formatters';
import { buildReportParams, reportRowView } from '../lib/financeReport';

const OPEN_STORAGE_KEY = 'admin:finance-report-open';

const readOpen = () => {
  try {
    return window.localStorage.getItem(OPEN_STORAGE_KEY) === '1';
  } catch (_) {
    return false;
  }
};

const storeOpen = (value) => {
  try {
    window.localStorage.setItem(OPEN_STORAGE_KEY, value ? '1' : '0');
  } catch (_) {
    // Tercih yalnızca kolaylıktır; yazılamazsa rapor kapalı açılır.
  }
};

const Pair = ({ count, amount, tone = '' }) => (
  <>
    <span className={`fin-report-amount ${tone}`}>{formatMoney(amount)}</span>
    <span className="fin-report-count">{formatNumber(count)} işlem</span>
  </>
);

/**
 * Yöntem bazlı yatırım/çekim raporu (günlük mutabakat).
 *
 * Finans Geçmişi'nin tarih, sağlayıcı ve tür süzgecini okur; satıra
 * tıklanınca liste o yönteme süzülür. Rapor yalnızca açıkken okunur —
 * kapalı bir panel için her canlı turda gruplu sorgu koşmak boşuna yüktür.
 */
const FinanceMethodReport = ({
  provider, tab, dateParams, refreshToken, activeMethod, onPick,
}) => {
  const [open, setOpen] = useState(readOpen);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const seqRef = useRef(0);
  const { toast } = useToast();

  const paramsKey = JSON.stringify(
    buildReportParams({ provider, tab, dateParams }));

  useEffect(() => {
    if (!open) return;
    seqRef.current += 1;
    const seq = seqRef.current;
    setLoading(true);
    client.get('/admin/transactions/method-report', {
      params: JSON.parse(paramsKey),
    })
      .then(({ data: body }) => {
        if (seq !== seqRef.current) return;
        setData(body);
        setError('');
      })
      .catch((requestError) => {
        if (seq !== seqRef.current) return;
        setError(requestError.response?.data?.detail
          || 'Yöntem raporu şu anda alınamıyor.');
      })
      .finally(() => {
        if (seq === seqRef.current) setLoading(false);
      });
  }, [open, paramsKey, refreshToken]);

  const rows = useMemo(
    () => (data?.items || []).map(reportRowView), [data]);
  const totals = useMemo(
    () => (data?.totals ? reportRowView(data.totals) : null), [data]);

  const toggle = () => {
    setOpen((current) => {
      storeOpen(!current);
      return !current;
    });
  };

  const download = async () => {
    setExporting(true);
    try {
      const response = await client.get(
        '/admin/transactions/method-report/export',
        { params: JSON.parse(paramsKey), responseType: 'blob' },
      );
      const disposition = response.headers?.['content-disposition'] || '';
      const match = /filename="([^"]+)"/.exec(disposition);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = match ? match[1] : 'finans-yontem-raporu.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) {
      toast({
        title: 'Rapor indirilemedi',
        description: 'Excel dosyası şu anda üretilemiyor; biraz sonra yeniden deneyin.',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className="fin-report" aria-label="Yöntem raporu">
      <div className="fin-report-head">
        <button
          type="button"
          className="fin-report-toggle"
          onClick={toggle}
          aria-expanded={open}
          aria-controls="finance-method-report"
        >
          <FileSpreadsheet size={15} aria-hidden="true" />
          <span>Yöntem Raporu</span>
          <span className="fin-report-hint">
            Hangi sağlayıcı ve yöntemden ne kadar yatırım/çekim — günlük mutabakat
          </span>
          <ChevronDown
            size={15}
            className={`fin-report-chevron ${open ? 'is-open' : ''}`}
            aria-hidden="true"
          />
        </button>
        {open && (
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={download}
            disabled={exporting || loading || rows.length === 0}
          >
            {exporting ? (
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
            ) : (
              <Download size={14} aria-hidden="true" />
            )}
            Excel'e aktar
          </button>
        )}
      </div>

      {open && (
        <div id="finance-method-report" className="fin-report-body">
          <p className="fin-report-note">
            Seçili tarih aralığı, sağlayıcı ve tür süzgecini gösterir; durum
            süzgeci uygulanmaz. Günlük mutabakat için tarihte <b>Bugün</b> ya
            da <b>Dün</b> seçin (Türkiye saati, 00:00–23:59). Satıra
            tıklayınca liste o yönteme süzülür.
          </p>
          {error ? (
            <p className="fin-report-error" role="alert">{error}</p>
          ) : loading && !data ? (
            <p className="fin-report-empty" role="status">
              <Loader2 size={14} className="animate-spin" aria-hidden="true" />
              Rapor okunuyor…
            </p>
          ) : rows.length === 0 ? (
            <p className="fin-report-empty">
              Seçili aralıkta bu süzgece uyan yatırım ya da çekim yok.
            </p>
          ) : (
            <div className="admin-table-wrap">
              <table className={`admin-table fin-report-table ${loading ? 'is-stale' : ''}`}>
                <thead>
                  <tr>
                    <th scope="col">Sağlayıcı / Yöntem</th>
                    <th scope="col" className="text-right">Onaylı yatırım</th>
                    <th scope="col" className="text-right">Bekleyen yatırım</th>
                    <th scope="col" className="text-right">Onaylı çekim</th>
                    <th scope="col" className="text-right">Bekleyen çekim</th>
                    <th scope="col" className="text-right">Reddedilen</th>
                    <th scope="col" className="text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const active = activeMethod
                      && activeMethod.provider === row.provider
                      && activeMethod.method === row.method;
                    return (
                      <tr
                        key={`${row.provider}|${row.method}`}
                        className={active ? 'is-active' : ''}
                      >
                        <th scope="row">
                          <button
                            type="button"
                            className="fin-report-method"
                            onClick={() => onPick(active ? null : row)}
                            aria-pressed={Boolean(active)}
                            title={active
                              ? 'Yöntem süzgecini kaldır'
                              : 'Listeyi bu yönteme süz'}
                          >
                            <span className="fin-report-provider">{row.providerLabel}</span>
                            <span className="fin-report-name">
                              {row.method || 'Yöntem adı yok'}
                            </span>
                            {active && <X size={12} aria-hidden="true" />}
                          </button>
                        </th>
                        <td className="text-right">
                          <Pair count={row.depositCount} amount={row.depositAmount} tone="is-in" />
                        </td>
                        <td className="text-right">
                          <Pair
                            count={row.depositPendingCount}
                            amount={row.depositPendingAmount}
                            tone="is-wait"
                          />
                        </td>
                        <td className="text-right">
                          <Pair count={row.withdrawCount} amount={row.withdrawAmount} tone="is-out" />
                        </td>
                        <td className="text-right">
                          <Pair
                            count={row.withdrawPendingCount}
                            amount={row.withdrawPendingAmount}
                            tone="is-wait"
                          />
                        </td>
                        <td className="text-right">
                          <Pair count={row.rejectedCount} amount={row.rejectedAmount} />
                        </td>
                        <td className="text-right">
                          <span className={`fin-report-amount ${row.net < 0 ? 'is-out' : ''}`}>
                            {formatMoney(row.net)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr>
                      <th scope="row">Toplam</th>
                      <td className="text-right">
                        <Pair count={totals.depositCount} amount={totals.depositAmount} tone="is-in" />
                      </td>
                      <td className="text-right">
                        <Pair
                          count={totals.depositPendingCount}
                          amount={totals.depositPendingAmount}
                          tone="is-wait"
                        />
                      </td>
                      <td className="text-right">
                        <Pair count={totals.withdrawCount} amount={totals.withdrawAmount} tone="is-out" />
                      </td>
                      <td className="text-right">
                        <Pair
                          count={totals.withdrawPendingCount}
                          amount={totals.withdrawPendingAmount}
                          tone="is-wait"
                        />
                      </td>
                      <td className="text-right">
                        <Pair count={totals.rejectedCount} amount={totals.rejectedAmount} />
                      </td>
                      <td className="text-right">
                        <span className={`fin-report-amount ${totals.net < 0 ? 'is-out' : ''}`}>
                          {formatMoney(totals.net)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default FinanceMethodReport;
